
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy } from "@angular/core";
import { CoreCOMService } from "../../services/com.service";
import { IOSHelperService } from "../../services/IOSHelper.service";
import { CoreLogService } from "../../services/log.service";
import { RouterHelperService } from "../../services/routerHelper.service";
import { WebShopService } from "../../services/webShop.service";
import { MainnavService, ViewType } from "../mainNav/mainnav.service";
import { OverlaySpinnerService } from "../overlayspinner/overlaySpiner.service";
import { CouponRedemptionService } from "../../services/couponRedemption.service";
import { RedemptionResult } from "../../shared/enums/coupon.enums";

@Component({
  selector: "couponCodeScanner",
  templateUrl: "./couponCodeScanner.component.html",
  styleUrls: ["./couponCodeScanner.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})

export class CouponCodeScannerComponent implements OnInit, OnDestroy {
  public isMobile: boolean = false;
  public isSmallView: boolean = false;

  public flashEnabled: boolean = false;
  public hasAccess: boolean = true;
  // browser scanner
  public zXingScanner: boolean;
  public hasDevices: boolean;
  public hasPermission: boolean;
  private scannerTimeOut: NodeJS.Timer

  constructor(private _changeDetectorRef: ChangeDetectorRef
    , private _coreLogService: CoreLogService
    , private _coreCOMService: CoreCOMService
    , private _mainnavService: MainnavService
    , private _iosHelperService: IOSHelperService
    , private _routerHelperService: RouterHelperService
    , private _webShopService: WebShopService
    , private _overlaySpinnerService: OverlaySpinnerService
    , private _couponRedemptionService: CouponRedemptionService
  ) {
    const me = this;
    me.isSmallView = me._mainnavService.viewType === ViewType.Small;
  }

  private PushChanges(): void {
    try {
      this._changeDetectorRef.detectChanges();
    } catch (e) {
      this._coreLogService.error("CouponCodeScannerComponent.PushChanges", "_changeDetectorRef.detectChanges (catch)", e);
    }
  }

  ngOnInit() {
    const me = this;
    me.isMobile = me._coreCOMService.environment.client.isMobile;
    if (me.isMobile) {
      me.scanMobileQRCode();
    } else {
      me._overlaySpinnerService.ShowLayoutSpinner();
      me.zXingScanner = true;
    }
  }

  ngOnDestroy() {
    const me = this;
    clearTimeout(me.scannerTimeOut);
    if (me.isMobile) {
      me._coreCOMService.environment.QRCodeScanner.destroy();
    } else {
      me.zXingScanner = false;
    }
  }

  //#region zxing-scanner

  /**zxing-scanner event when camera found
   * @devices find back camera in first try. if no back camera found return the last camera from the list of cameras
   */
  onCamerasFound(devices: MediaDeviceInfo[]): void {
    const me = this;
    me.hasDevices = Boolean(devices && devices.length > 0);
    if (!me.hasDevices) {
      me.onBrowserCameraErrorHandler();
    } else {
      setTimeout(() => {
        me._overlaySpinnerService.HideLayoutSpinner();
      }, 1000);
    }
  }

  /**define browser access
 * @has by zXing/qrcode scanner plugin after initialization
 */
  public onPermissionResponse(has: boolean): void {
    const me = this;
    me.hasAccess = has;
    if (!has) {
      me._overlaySpinnerService.HideLayoutSpinner();
    }
  }

  /**zxing-scanner handle error on camera device */
  private onBrowserCameraErrorHandler() {
    const me = this;
    me.hasAccess = false;
  }

  /**zxing-scanner handle error when no camerafound */
  public onCamerasnotfound() {
    const me = this;
    me.onBrowserCameraErrorHandler();
    me._coreLogService.error("CouponCodeScannerComponent", "onCamerasnotfound");
  }

  /**zxing-scanner handle error for scanning */
  public scanErrorHandler($event) {
    const me = this;
    me.onBrowserCameraErrorHandler();
    me._coreLogService.error("CouponCodeScannerComponent", "scanErrorHandler", $event);
  }

  /**zxing-scanner handle error fail in scanning */
  public scanFailureHandler($event) {
    const me = this;
    me.onBrowserCameraErrorHandler();
    me._coreLogService.error("CouponCodeScannerComponent", "scanFailureHandler", $event);
  }

  /**zxing-scanner return qrcode scanned by camera
   * @result a string that scanned by qrcode-scanner
   */
  public scanCompleteHandler(result) {
    const me = this;
    me.zXingScanner = false;
    me.scanDone(result.text);
  }

  public async scanDone(scan: string): Promise<void> {
    const me = this;
    var scannedCouponCode: string = "none";
    try {
      scannedCouponCode = new URL(scan.trim()).searchParams.get("HYAR");
    } catch (error) {
      // test if data is old format
      if (scan.startsWith("HYAR=")) {
        scannedCouponCode = scan.substring(5)
      } else {
        me._coreLogService.error("CouponCodeScannerComponent", "catch parse data", scan, error);
      }
    }
    if (scannedCouponCode && scannedCouponCode !== "none") {
      await me.openCouponProductByScanningCoupon(scannedCouponCode);
    } else {
      me._couponRedemptionService.openWrongCodeDialog(() => { me.resetScanner(); me.PushChanges(); });
    }
  }

  public turnOnFlashLight() {
    const me = this;
    me._coreCOMService.environment.QRCodeScanner.enableLight();
    me.flashEnabled = true;
    me.PushChanges();
  }

  public turnOffFlashLight() {
    const me = this;
    me._coreCOMService.environment.QRCodeScanner.disableLight();
    me.flashEnabled = false;
    me.PushChanges();
  }

  private scanMobileQRCode() {
    const me = this;
    me._overlaySpinnerService.ShowLayoutSpinner();
    me._coreCOMService.environment.QRCodeScanner.prepare()
      .then(_ => {
        me._coreCOMService.environment.QRCodeScanner.show()
          .then(_ => {
            me._overlaySpinnerService.HideLayoutSpinner();
            me.scannerTimeOut = setTimeout(() => {
              me._coreCOMService.environment.QRCodeScanner.destroy()
                .then((status) => {
                  me.PushChanges();
                  me._couponRedemptionService.openWrongCodeDialog(() => { me.resetScanner(); me.PushChanges(); });
                })
            }, 30000);
            me._coreCOMService.environment.QRCodeScanner.scan()
              .then(contents => {
                clearTimeout(me.scannerTimeOut);
                me._coreCOMService.environment.QRCodeScanner.destroy();
                me.scanDone(contents);
              })
              .catch(error => {
                me._coreCOMService.environment.QRCodeScanner.destroy();
                me.hasAccess = false;
                me.PushChanges();
                me._coreLogService.error("CouponCodeScannerComponent.scan()", " QRCodeScanner.scan()", error);
              });
          })
          .catch(error => {
            me._overlaySpinnerService.HideLayoutSpinner();
            me._coreCOMService.environment.QRCodeScanner.destroy();
            me.hasAccess = false;
            me.PushChanges();
            me._coreLogService.error("CouponCodeScannerComponent.scan()", " QRCodeScanner.show()", error);
          })
      })
      .catch(error => {
        me._overlaySpinnerService.HideLayoutSpinner();
        me._coreCOMService.environment.QRCodeScanner.destroy();
        me.hasAccess = false;
        me.PushChanges();
        me._coreLogService.error("CouponCodeScannerComponent.scan()", " QRCodeScanner.prepare()", error);
      });
  }

  public cancelScan() {
    const me = this;
    clearTimeout(me.scannerTimeOut);
    if (me.isMobile) {
      me._coreCOMService.environment.QRCodeScanner.destroy();
    } else {
      me.zXingScanner = false;
    }
    me._overlaySpinnerService.HideLayoutSpinner();
    me.navigateBack();
  }

  /** Thin delegator — resolves a raw scanned code through the shared funnel. */
  public async openCouponProductByScanningCoupon(couponCode: string): Promise<void> {
    const me = this;
    const result = await me._couponRedemptionService.resolveCouponCodeRedemption(couponCode);
    me.handleRedemptionResult(result);
  }

  /** Routes a RedemptionResult to the shared dialog router; resolveCouponCodeRedemption only ever yields Proceed/WrongCode/NotAvailable/InvalidCode, so routeRedemptionResult alone covers every reachable case. */
  private handleRedemptionResult(result: RedemptionResult): void {
    const me = this;
    const afterDialog = () => { me.resetScanner(); me.PushChanges(); };
    me._couponRedemptionService.routeRedemptionResult(result, afterDialog);
  }

  private resetScanner() {
    const me = this;
    if (me.isMobile) {
      me.scanMobileQRCode();
    } else {
      me.zXingScanner = true;
    }
  }

  public setWebView() {
    const me = this;
    me._iosHelperService.setWebviewAfterBlur();
  }

  public handleNavigateBack() {
    const me = this;
    me.navigateBack();
  }

  private navigateBack(): void {
    const me = this;
    me._routerHelperService.navigateBackSimple();
  }

  public SwiperNavigateBack() {
    const me = this;
    if (me.isSmallView == true) {
      me.handleNavigateBack();
    }
  }
}